import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "./command";
import { Button } from "./button";
import { ChevronsUpDown, Check } from "lucide-react";

import { cn } from "@/lib/utils";

interface BaseProps {
  values: string[];
  setValues: (values: string[]) => void;
  max?: number;
}

interface LabeledOption {
  label: string;
  value: string;
}
interface GroupedOption {
  group: string;
  options: readonly LabeledOption[];
}

type OptionProps =
  | { options: readonly string[]; labeledOptions?: never }
  | {
      options?: never;
      labeledOptions: readonly LabeledOption[];
    };

type Props = BaseProps & OptionProps;
type GroupedProps = BaseProps & {
  groupedOptions: GroupedOption[];
};

export const MultiSelect = ({
  values,
  setValues,
  options,
  labeledOptions,
  max,
}: Props) => {
  // If using labeled options, convert the values array to match the labels
  // (for display purposes)
  const labeledValues = labeledOptions
    ? values.map(
        (value) =>
          labeledOptions.find((option) => option.value === value)?.label,
      )
    : values;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn(
            "w-full h-auto flex flex-row items-center justify-between",
            !labeledValues && "text-muted-foreground",
            "text-wrap text-left",
          )}
          childFullWidth
        >
          {labeledValues.length > 0
            ? labeledValues.join(", ")
            : "Select a value"}
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" disablePortal>
        <Command>
          <CommandInput placeholder="Search values..." />
          <CommandList>
            <CommandEmpty>No value found.</CommandEmpty>
            <CommandGroup>
              {labeledOptions
                ? labeledOptions.map(({ label, value }) => (
                    <CommandItem
                      value={value}
                      key={value}
                      onSelect={() => {
                        if (values.includes(value)) {
                          setValues(values.filter((v) => v !== value));
                        } else {
                          setValues([...values, value]);
                        }
                      }}
                      disabled={Boolean(
                        max && values.length >= max && !values.includes(value),
                      )}
                    >
                      {label}
                      <Check
                        className={cn(
                          "ml-auto",
                          values.includes(value) ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))
                : options.map((option) => (
                    <CommandItem
                      value={option}
                      key={option}
                      onSelect={() => {
                        if (values.includes(option)) {
                          setValues(values.filter((value) => value !== option));
                        } else {
                          setValues([...values, option]);
                        }
                      }}
                      disabled={Boolean(
                        max && values.length >= max && !values.includes(option),
                      )}
                    >
                      {option}
                      <Check
                        className={cn(
                          "ml-auto",
                          values.includes(option) ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const GroupedMultiSelect = ({
  values,
  setValues,
  groupedOptions,
  max,
}: GroupedProps) => {
  const valueToLabelMap: Record<string, string> = {};
  groupedOptions.forEach(({ options }) =>
    options.forEach(({ label, value }) => (valueToLabelMap[value] = label)),
  );

  const labeledValues = values.map((value) => valueToLabelMap[value]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn(
            "w-full h-auto flex flex-row items-center justify-between",
            "text-wrap text-left",
          )}
          childFullWidth
        >
          {labeledValues.length > 0
            ? labeledValues.join(", ")
            : "Select a value"}
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" disablePortal>
        <Command>
          <CommandInput placeholder="Search values..." />
          <CommandList>
            <CommandEmpty>No value found.</CommandEmpty>

            {groupedOptions.map(({ group, options }) => (
              <CommandGroup key={group}>
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {group}
                </div>
                {options.map(({ label, value }) => (
                  <CommandItem
                    value={value}
                    key={`${group}.${value}`}
                    onSelect={() => {
                      if (values.includes(value)) {
                        setValues(values.filter((v) => v !== value));
                      } else {
                        setValues([...values, value]);
                      }
                    }}
                    disabled={Boolean(
                      max && values.length >= max && !values.includes(value),
                    )}
                  >
                    {label}
                    <Check
                      className={cn(
                        "ml-auto",
                        values.includes(value) ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
